import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Dialog, DialogContent, DialogTitle } from '../../ui/dialog';
import { X, Plus, ArrowRight, Sparkles } from 'lucide-react';
import AddonCard from './AddonCard';
import { buildTokens, tokensToCssVars } from './storefrontTheme';
import './storefront.css';

/**
 * AddonsOfferModal
 *
 * Cinematic upsell modal shown AFTER a customer adds a main meal plan to cart,
 * IF the tenant has add-on meal plans available (is_meal_addon or is_delivery_addon).
 *
 * The customer can:
 *  - Tap an add-on to open its config in AddToCartModal (delegated via onSelectAddon)
 *  - Skip and go directly to checkout
 *  - Continue shopping (close the modal)
 *
 * Design matches the cinematic storefront: Playfair + Caveat typography, primary_color
 * accents, cream card body, rounded pill CTAs.
 */
const AddonsOfferModal = ({
  open,
  onClose,
  addons = [],
  onSelectAddon,
  onContinueToCheckout,
  brandingHint = null,
  brand = null,
}) => {
  // v5.66 — tokens on the dialog's own node (it portals outside the page shell)
  const tokens = buildTokens(brand || brandingHint || {});
  const primary = tokens.primary;
  const accent = tokens.accent;

  if (!addons.length) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="p-0 overflow-hidden max-w-2xl border-0 shadow-2xl sf-root"
        data-testid="addons-offer-modal"
        style={{ ...tokensToCssVars(tokens), borderRadius: 'calc(var(--sf-radius) * 1.2)' }}
      >
        <DialogTitle className="sr-only">Add extras to your order</DialogTitle>

        <button
          type="button"
          onClick={onClose}
          data-testid="addons-offer-close"
          className="sf-icon-btn absolute top-3 right-3 z-10 !w-9 !h-9"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        <AnimatePresence mode="wait">
          <motion.div
            key="addons"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          >
            {/* Header stripe */}
            <div className="relative h-20 overflow-hidden" style={{ background: primary }}>
              <div className="absolute inset-0 flex items-center justify-center">
                <Sparkles className="w-10 h-10 opacity-40" strokeWidth={1.2} style={{ color: 'var(--sf-on-primary)' }} />
              </div>
            </div>

            <div className="px-8 pt-6 pb-8">
              <span className="sf-eyebrow !mb-2">
                One more thing
              </span>
              <h3
                className="leading-tight mb-1"
                style={{
                  fontFamily: 'var(--sf-heading)',
                  fontWeight: 500,
                  fontSize: 'clamp(1.5rem, 3vw, 2rem)',
                }}
              >
                Care to add some{' '}
                <span className="sf-highlight" style={{ color: primary }}>
                  extras?
                </span>
              </h3>
              <p className="text-sm text-neutral-600 leading-relaxed mt-2">
                A quick upsell before we take you to checkout — these pair perfectly with what's already in your cart.
              </p>

              {/* Add-on cards — shared AddonCard (v4.12) so this upsell and
                  the pre-add-to-cart section can't drift. */}
              <div className="mt-6 space-y-3 max-h-[45vh] overflow-y-auto pr-1">
                {addons.map((addon) => (
                  <AddonCard key={addon.id} addon={addon} primary={primary} onAdd={onSelectAddon} />
                ))}
              </div>

              {/* Actions */}
              <div className="mt-6 flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  data-testid="addons-offer-skip"
                  className="flex-1 sf-btn sf-btn-ghost"
                >
                  Continue shopping
                </button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="button"
                  onClick={onContinueToCheckout}
                  data-testid="addons-offer-checkout"
                  className="flex-1 sf-btn sf-btn-primary"
                >
                  To checkout
                  <ArrowRight className="w-4 h-4" />
                </motion.button>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};

export default AddonsOfferModal;
